import * as React from 'react'

import { useSiteConfig, useSiteDefinition } from '@thg-commerce/enterprise-core'

import { useProductPage } from '../../context/ProductPageContext'

const stripHTML = (value: string): string => {
  return value.replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, '')
}

enum ProductAvailability {
  IN_STOCK = 'InStock',
  OUT_OF_STOCK = 'OutOfStock',
  PREORDER = 'Preorder',
}

const productStatus = (stock: boolean, preorder: boolean) => {
  const availability = stock
    ? ProductAvailability.IN_STOCK
    : ProductAvailability.OUT_OF_STOCK
  return preorder ? ProductAvailability.PREORDER : availability
}

const sku = (
  sku: string,
  externalIdentifier?: string | null,
  useExternalIdentifierInSchema?: boolean,
) => {
  if (externalIdentifier && useExternalIdentifierInSchema) {
    return externalIdentifier
  }
  return sku
}

// TODO: CSBOM-403 to move the logic to the resolver
const createOfferSchema = (
  product,
  preOrder,
  variant = false,
  url,
  defaultSessionSettings,
  useExternalIdentifierInSchema,
) => {
  let productUrl = `${url}?shippingcountry=${defaultSessionSettings.shippingDestination}&switchcurrency=${defaultSessionSettings.currency}`
  if (variant) {
    productUrl += `&variation=${sku(
      product.sku.toString(),
      product.externalIdentifier,
      useExternalIdentifierInSchema,
    )}`
  }
  return {
    '@type': 'Offer',
    availability: `https://schema.org/${productStatus(
      product.defaultVariant?.inStock ?? product.inStock,
      preOrder,
    )}`,
    itemCondition: 'https://schema.org/NewCondition',
    price: variant ? product.price?.price.amount : product.priceAmount,
    priceCurrency: defaultSessionSettings.currency,
    sku: sku(
      product.sku.toString(),
      product.externalIdentifier,
      useExternalIdentifierInSchema,
    ),
    url: productUrl,
  }
}

const reviewsSchema = (
  product,
  useExternalIdentifierInSchema,
  showLinkedorChildSKUinSchema,
  url,
  defaultSessionSettings,
  showPowerReview,
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': sku(
      product.sku.toString(),
      product.externalIdentifier,
      useExternalIdentifierInSchema,
    ),
    sku: sku(
      product.sku.toString(),
      product.externalIdentifier,
      useExternalIdentifierInSchema,
    ),
    ...(!showPowerReview && product?.reviews && product.reviews.total > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.reviews?.averageScore?.toString(),
            reviewCount: product.reviews?.total?.toString(),
          },
        }
      : {}),
    ...(showPowerReview && product?.powerReviews?.reviews.length
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.powerReviews?.reviewRating?.toString(),
            ratingCount: product.powerReviews?.reviewCount?.toString(),
          },
        }
      : {}),
    description: stripHTML(product.description?.items[0]?.content || ''),
    brand: {
      '@type': 'Brand',
      name: product.brand?.name,
    },
    image:
      product.defaultVariant?.images[0]?.largeProduct ||
      product.images[0]?.largeProduct,
    name: product.title,
    offers: showLinkedorChildSKUinSchema
      ? product.variants.map((variant) =>
          createOfferSchema(
            variant,
            product.preorder,
            true,
            url,
            defaultSessionSettings,
            useExternalIdentifierInSchema,
          ),
        )
      : [
          createOfferSchema(
            product,
            product.preorder,
            false,
            url,
            defaultSessionSettings,
            useExternalIdentifierInSchema,
          ),
        ],
    review: product?.powerReviews?.reviews
      ? product?.powerReviews?.reviews
      : null,
  }
}

export const ProductSchema = () => {
  const { product } = useProductPage()
  const { domain, defaultSessionSettings } = useSiteDefinition()
  const {
    useExternalIdentifierInSchema,
    showLinkedorChildSKUinSchema,
    showPowerReview,
  } = useSiteConfig()

  if (!product?.sku) {
    return null
  }

  return (
    <script
      type="application/ld+json"
      data-testid="product-schema"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(
          reviewsSchema(
            product,
            useExternalIdentifierInSchema,
            showLinkedorChildSKUinSchema,
            `https://${domain}${product.url}`,
            defaultSessionSettings,
            showPowerReview,
          ),
        ),
      }}
    />
  )
}
